import { CircularProgress, Box } from '@mui/material'
import { useVirtualizer } from '@tanstack/react-virtual'
import { useState, useEffect, useRef, useEffectEvent } from 'react'
import type { FC } from 'react'
import { ProviderListItem, ProviderDetailWithAdditionalData } from './ProviderListItem'
import {
  referralSearchRibbonProviders,
  referralSearchRibbonFacilities,
  type ReferralSearchRibbonProvidersCreateResponse,
  type ReferralSearchRibbonFacilitiesCreateResponse,
  type StatesListResponse,
} from '@fireflyhealth/api'
import { useDispatch } from 'react-redux'
import { queueNotification } from '~/shared/notifications/actions'
import { SpecialtyGroupSearchCategory, LocationTypeSearchCategory } from './ProviderSearchContent'
import { ClinicalFocusArea, SteerageProvider } from '~/entities/steerage'
import { EventType, logEvent, SteerageSearchEventPayload } from '~/shared/lib/events'
import { CPTCode } from './types'
import type { Person } from '~/shared/types'
import { logger } from '~/shared/lib/logger'

const PROVIDER_SEARCH_PAGE_SIZE_HUNDRED = 100

interface ProviderListProps {
  person: Person
  locationType: LocationTypeSearchCategory | null
  specialtyGroup: SpecialtyGroupSearchCategory | null
  zipCode: string | null | undefined
  distance: number
  npi: string | null
  providerName: string | null
  gender: string | null
  facilityName: string | null
  procedureCode: CPTCode | null
  steerageId: number | null
  resultCountCallback: (count: number, hasMore: boolean) => void
  loadingResultsCallback: (val: boolean) => void
  steerageProviderList?: Array<SteerageProvider>
  states?: StatesListResponse
  providerSearchType: 'provider' | 'facility' | 'procedure'
  searchAvailabilityId: number | undefined
  clinicalFocusAreas?: ClinicalFocusArea[]
}

export const ProviderList: FC<ProviderListProps> = (props: ProviderListProps) => {
  const dispatch = useDispatch()
  const parentRef = useRef<HTMLDivElement>(null)

  const page_size = PROVIDER_SEARCH_PAGE_SIZE_HUNDRED

  // Derived values from the progressive search that are used to control page functionality
  const [loadingProviders, setLoadingProviders] = useState<boolean>(false)
  const [isFacilityList, setIsFacilityList] = useState<boolean>(
    !!props.locationType || !!props.facilityName,
  )
  const [currentPageNumber, setCurrentPageNumber] = useState<number>(0)
  const [hasNextPage, setHasNextPage] = useState<boolean>(false)
  const [providers, setProviders] = useState<ProviderDetailWithAdditionalData[]>([])
  const [searchRequestId, setSearchRequestId] = useState<number | null>(null)

  const { resultCountCallback, loadingResultsCallback } = props

  // Ribbon's records_available counts providers, while the list holds one row per provider
  // location and drops some providers server-side, so only the loaded rows are a true count.
  useEffect(() => {
    resultCountCallback(providers.length, hasNextPage)
  }, [providers.length, hasNextPage, resultCountCallback])

  // An effect event: the paging effects below fire on the result list and the virtualizer, and
  // read the current page, loading flag and search props through this without re-running for them.
  const fetchNextPage = useEffectEvent(async () => {
    if (loadingProviders) {
      return
    }
    const nextPageNumber = currentPageNumber + 1
    setLoadingProviders(true)

    try {
      let npi: string[] | null = null
      if (props.npi == null || props.npi.length === 0) {
        npi = null
      } else {
        npi = [props.npi]
      }
      let searchResults:
        ReferralSearchRibbonProvidersCreateResponse | ReferralSearchRibbonFacilitiesCreateResponse
      const requestStartTime = Date.now()

      if (props?.providerSearchType == 'provider') {
        const response = await referralSearchRibbonProviders.create({
          data: {
            person: props.person.id,
            address: props.zipCode || null,
            specialty_group: props.specialtyGroup?.id ?? null,
            clinical_focus_areas: props.clinicalFocusAreas?.map(c => c.id) ?? null,
            distance: props.distance,
            page: nextPageNumber,
            page_size: page_size,
            npis: npi,
            name: props.providerName,
            gender: props.gender,
            cpt_code: props.procedureCode?.id ?? null,
            steerage: props?.steerageId ?? null,
            search_availability_id: props.searchAvailabilityId ?? null,
          },
        })
        searchResults = response.data
      } else {
        const response = await referralSearchRibbonFacilities.create({
          data: {
            person: props.person.id,
            address: props.zipCode || null,
            location_type: props.locationType?.id ?? null,
            distance: props.distance,
            page: nextPageNumber,
            page_size: page_size,
            facility_name: props.facilityName,
            steerage: props?.steerageId ?? null,
            search_availability_id: props.searchAvailabilityId ?? null,
          },
        })
        searchResults = response.data
      }
      setSearchRequestId(searchResults.id)

      const results = searchResults.results ?? []

      let eventType: EventType = 'SEARCH_REQUEST'
      if (props.steerageId != null) {
        eventType = 'STEERAGE_SEARCH_REQUEST'
      }
      const requestDurationInSeconds = (Date.now() - requestStartTime) / 1000
      void logEvent<SteerageSearchEventPayload>(eventType, {
        steerageId: props.steerageId,
        searchRequestId: searchResults.id,
        pageSize: page_size,
        numberOfProviders: results.length,
        requestDurationInSeconds: requestDurationInSeconds,
        SearchAvailabilityId: props?.searchAvailabilityId ?? null,
      })
      setCurrentPageNumber(nextPageNumber)
      setProviders(p => p.concat(results))
      // There exists a next page of results in ribbon
      // if the current page multiplied by the page size (which is the total number
      // of results loaded from ribbon) is less than the total number of ribbon results
      setHasNextPage((searchResults.records_available ?? 0) > page_size * nextPageNumber)
    } catch (e) {
      dispatch(
        queueNotification({
          message: 'Unable to retrieve search results',
          variant: 'error',
        }),
      )
      logger.error('[ProviderSearch] retrieve search results', e)
    }
    setLoadingProviders(false)
  })

  // Fetch the first page of a fresh search, and keep auto-paging through an empty
  // result list only while Ribbon reports more results are available. Without the
  // hasNextPage guard this effect re-fires on every (new-reference) empty results
  // array and walks the page number up past the last real page until Ribbon 400s
  // on an out-of-range page.
  useEffect(() => {
    if (providers.length === 0 && (currentPageNumber === 0 || hasNextPage)) {
      void fetchNextPage()
    }
  }, [providers, currentPageNumber, hasNextPage])

  // Use the parent's loadingResultsCallback to show a loading indicator when we're loading providers the first time
  useEffect(() => {
    if ((!providers.length && loadingProviders) || !loadingProviders) {
      loadingResultsCallback(loadingProviders)
    }
  }, [loadingProviders, providers.length, loadingResultsCallback])

  // Reset search whenever a search term changes
  useEffect(() => {
    setProviders([])
    setIsFacilityList(!!props.locationType || !!props.facilityName)
    setCurrentPageNumber(0)
    setHasNextPage(false)
  }, [
    props.locationType,
    props.specialtyGroup,
    props.distance,
    props.zipCode,
    props.facilityName,
    props.npi,
    props.providerName,
    props.gender,
    props.procedureCode,
  ])

  // eslint-disable-next-line react-hooks/incompatible-library -- the virtualizer is mutable by design
  const virtualizer = useVirtualizer({
    count: providers.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => (isFacilityList ? 200 : 245),
    overscan: 10,
  })

  const virtualItems = virtualizer.getVirtualItems()

  // Handle infinite loading
  const lastItemIndex = providers.length - 1
  useEffect(() => {
    const lastItem = virtualItems.at(-1)

    // If we're near the end and there's a next page to load, fetch it
    if (lastItem && !loadingProviders && hasNextPage && lastItem.index >= lastItemIndex) {
      void fetchNextPage()
    }
  }, [virtualItems, lastItemIndex, hasNextPage, loadingProviders])

  return (
    <Box ref={parentRef} sx={{ flex: 1, overflow: 'auto', height: '100%' }}>
      {providers.length > 0 && (
        <Box sx={{ height: virtualizer.getTotalSize(), width: '100%', position: 'relative' }}>
          {virtualItems.map(virtualRow => {
            const provider = providers[virtualRow.index]
            if (!provider) return null
            return (
              <Box
                key={`provider-${virtualRow.index}`}
                data-index={virtualRow.index}
                ref={virtualizer.measureElement}
                style={{ transform: `translateY(${virtualRow.start}px)` }}
                sx={{ position: 'absolute', top: 0, left: 0, width: '100%' }}
              >
                <ProviderListItem
                  provider={provider}
                  locationType={props.locationType?.label}
                  npi={props.npi}
                  providerName={props.providerName}
                  facilityName={props.facilityName}
                  steerageProviderList={props.steerageProviderList}
                  steerageID={props.steerageId}
                  states={props.states}
                  specialtyGroupSearchCategory={props.specialtyGroup}
                  providerSearchType={props.providerSearchType}
                  indexInResults={virtualRow.index}
                  searchRequestId={searchRequestId}
                  cptCode={null}
                />
                {/** Show loading indicator when we're at the end of the list and we're loading more providers */}
                {virtualRow.index === providers.length - 1 && loadingProviders ? (
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2 }}
                  >
                    <CircularProgress size={20} />
                    <Box sx={{ ml: 2 }}>Loading more providers...</Box>
                  </Box>
                ) : null}
              </Box>
            )
          })}
        </Box>
      )}
    </Box>
  )
}
